import type { EventAttendanceRowDto } from '../../../api/engagementMetrics';

interface EngagementMetricsChartProps {
  rows: EventAttendanceRowDto[];
  period1Year: number;
  period2Year: number;
}

const COLOR_P1 = '#1a3bcc';
const COLOR_P2 = '#90caf9';
const DENOMINATOR_TOOLTIP_TEXT = "Percentage is based on the BRG's current membership count.";

function formatLastUpdated(loggedAt: string | null): string {
  if (!loggedAt) return '';
  const d = new Date(loggedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function maxAttendees(rows: EventAttendanceRowDto[]): number {
  let max = 0;
  for (const r of rows) {
    if (r.period1 && r.period1.actualAttendees > max) max = r.period1.actualAttendees;
    if (r.period2 && r.period2.actualAttendees > max) max = r.period2.actualAttendees;
  }
  return Math.max(max, 1);
}

// AC22 / AC22a / AC22b / AC14c — dual horizontal bar chart with the two tooltips.
// AG Charts is used elsewhere (see budget/BudgetVsSpendingChart.tsx) but custom
// per-element tooltips on inline labels are simpler with a hand-rolled SVG-ish
// layout here. The bar geometry uses CSS flex widths normalized to maxAttendees.
export function EngagementMetricsChart({ rows, period1Year, period2Year }: EngagementMetricsChartProps) {
  if (rows.length === 0) {
    return null;
  }

  const max = maxAttendees(rows);

  return (
    <div data-testid="engagement-metrics-chart" style={{ background: '#fff', borderRadius: '6px', padding: '12px', border: '1px solid #e0e0e0' }}>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '12px', color: '#333' }}>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: COLOR_P1, marginRight: 6, verticalAlign: 'middle' }} aria-hidden="true" />
          Period 1 ({period1Year})
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: COLOR_P2, marginRight: 6, verticalAlign: 'middle' }} aria-hidden="true" />
          Period 2 ({period2Year})
        </span>
      </div>

      {rows.map((row, idx) => (
        <div
          key={`${row.brgId}-${row.eventName}-${idx}`}
          data-testid="chart-row"
          data-event-name={row.eventName}
          data-brg-id={row.brgId}
          style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '8px', alignItems: 'center', padding: '0', fontSize: '12px', marginBottom: '6px' }}
        >
          <div title={row.eventName} style={{ textAlign: 'right', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.eventName}
          </div>
          <div>
            <Bar
              period={row.period1}
              color={COLOR_P1}
              max={max}
              memberCount={row.brgCurrentMembershipCount}
              testIdPrefix={`bar-p1-${row.brgId}`}
            />
            <Bar
              period={row.period2}
              color={COLOR_P2}
              max={max}
              memberCount={row.brgCurrentMembershipCount}
              testIdPrefix={`bar-p2-${row.brgId}`}
            />
          </div>
        </div>
      ))}
    </div>
  );

  // Inline component — keeps the rendering local to the chart.
  function Bar({
    period,
    color,
    max,
    memberCount,
    testIdPrefix,
  }: {
    period: EngagementMetricsChartProps['rows'][number]['period1'];
    color: string;
    max: number;
    memberCount: number;
    testIdPrefix: string;
  }) {
    if (!period) {
      return (
        <div style={{ height: '16px' }} data-testid={`${testIdPrefix}-empty`} />
      );
    }

    const widthPct = Math.max((period.actualAttendees / max) * 60, 2);
    const lastUpdatedTooltip = period.lastUpdatedBy && period.lastUpdatedAt
      ? `Last updated by ${period.lastUpdatedBy.fullName} on ${formatLastUpdated(period.lastUpdatedAt)}`
      : null;

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '16px' }}
        data-testid={testIdPrefix}
      >
        <div
          style={{
            width: `${widthPct}%`,
            background: color,
            height: '12px',
            borderRadius: '2px',
            minWidth: '4px',
            flexShrink: 0,
          }}
        />
        <span
          data-testid={`${testIdPrefix}-count`}
          title={lastUpdatedTooltip ?? undefined}
          tabIndex={lastUpdatedTooltip ? 0 : -1}
          style={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}
        >
          {period.actualAttendees}
        </span>
        <span
          data-testid={`${testIdPrefix}-percent`}
          title={DENOMINATOR_TOOLTIP_TEXT}
          tabIndex={0}
          style={{ color: '#666', whiteSpace: 'nowrap', fontSize: '11px' }}
        >
          ({period.attendancePercent.toFixed(1)}% of {memberCount})
        </span>
      </div>
    );
  }
}
